import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { of } from 'rxjs';

import { FileManagerUploadedFilesComponent } from '@features/file-manager-uploaded-files/file-manager-uploaded-files.component';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { FileMetadata } from '@shared/services/api.service';
import { DocumentType, EntityType, Note, User } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { batchPromises } from '@shared/utils/batch-promises';
import { Utils } from '@shared/utils/utils';

export type AdjustmentModalDataType = {
  activity_by_vendor: Record<string, string[]>;
  current_month: string;
  notes: Note[];
  users: Map<string, Pick<User, 'email' | 'family_name' | 'given_name'>>;
};

export type AdjustmentModalResponseType = undefined | { documents: number; note: string };

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    FormsModule,
    ReactiveFormsModule,
    FileManagerComponent,
    FileManagerUploadedFilesComponent,
    DatePipe,
  ],
  selector: 'aux-adjustment-modal',
  styles: [],
  templateUrl: './adjustment-modal.component.html',
})
export class AdjustmentModalComponent implements OnInit {
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  uploadedFiles$ = of<File[]>([]);

  @ViewChild('fileManager') fileManager?: FileManagerComponent;
  metadata: FileMetadata = { documentType: DocumentType.DOCUMENT_CLOSE_SUMMARY };
  notes: (Note & {
    username: string;
  })[] = [];
  ref =
    inject<CustomOverlayRef<AdjustmentModalResponseType, AdjustmentModalDataType>>(
      CustomOverlayRef
    );
  textarea = new FormControl('', Validators.required);

  ngOnInit(): void {
    Promise.resolve()
      .then(() => {
        if (this.fileManager) {
          this.uploadedFiles$ = this.fileManager.fileQuery.selectAll();
        }
      })
      .catch(console.error);
  }

  close(bool: boolean) {
    const note = this.textarea.value;

    if (bool && !note) {
      this.textarea.markAsTouched();
      this.textarea.markAsDirty();
      this.textarea.updateValueAndValidity();

      return;
    }

    this.uploadFiles()
      .then((uploadedDocuments) => {
        this.ref.close({
          documents: uploadedDocuments,
          note: note as string,
        });
      })
      .catch(console.error);
  }

  constructor() {
    this.notes = (this.ref.data?.notes || []).map((note) => {
      const user = this.ref.data?.users.get(note.created_by);

      return {
        ...note,
        message: Utils.unscrubUserInput(note.message),
        username: `${user?.given_name} ${user?.family_name}`,
      };
    });
  }

  onRemoveFile(file: File) {
    const success = this.fileManager?.removeFile(file);

    if (success) {
      this.overlayService.success(`${file.fileName} removed!`);
    }
  }

  pathFn = () => {
    const trialId = this.mainQuery.getValue().trialKey;

    return () => `trials/${trialId}/month-close-manual-adjustment/${this.ref.data?.current_month}/`;
  };

  async uploadFiles(): Promise<number> {
    const trialId = this.mainQuery.getValue().trialKey;

    if (this.fileManager) {
      const promises: Promise<boolean>[] = [];

      Object.entries(this.ref.data?.activity_by_vendor || {}).forEach(
        ([vendor_id, activity_ids]) => {
          if (!vendor_id) return;

          const metadata = activity_ids.map((entity_id) => {
            return {
              documentType: DocumentType.DOCUMENT_MANUAL_ADJUSTMENT,
              entity_id: entity_id,
              entity_type_id: EntityType.ACTIVITY,
              target_date: this.ref.data?.current_month || '',
              vendor: vendor_id,
            };
          });

          promises.push(
            this.fileManager!.fileService.batchUploadFiles(
              metadata,
              false,
              `trials/${trialId}/month-close-manual-adjustment/${this.ref.data?.current_month}/${vendor_id}/`,
              false,
              false,
              true
            )
          );
        }
      );

      await batchPromises(promises, (p) => p);

      const uploadedFiles = this.fileManager.fileQuery.getAll({
        filterBy: (entity) => entity.uploaded,
      });

      return uploadedFiles.length;
    }

    return 0;
  }
}
